<script lang="ts">
    import {startOfToday, isWeekend, subDays, isSameDay, isToday} from "date-fns";
    import { zonedTimeToUtc } from "date-fns-tz";
    import { getAppContext } from "../../AppContext";
    const {feGatewaySdk, buildingCode, buildingCodeDemoList} = getAppContext();
    import type {GetTenantBreakdownByBucketQuery, GetTenantBreakdownSummedQuery} from "../../frontendGatewaySdk";

    import HorizontalBarGraph from "@cohesionib/biblioteca/lib/HorizontalBarGraph";
    import TabPage from "@cohesionib/biblioteca/lib/TabPage/TabPage.svelte";
    import SubPage from "@cohesionib/biblioteca/lib/TabPage/SubPage.svelte";
    import Snapshot from "../../shared/SnapshotSection.svelte";
    import Modal from '../../shared/Modal.svelte';
    import tenantBreakdownImage from '../../images/tenantBreakdownExplanation.png'

    export let rangeDays: number = 0;
    export let pickerStartDate: Date = null;
    export let pickerEndDate: Date = null;
    export let includeWeekends: boolean = false;

    let tenantBreakdownByBucketData: GetTenantBreakdownByBucketQuery | null = null;
    let tenantBreakdownSummedData: GetTenantBreakdownSummedQuery | null = null;
    let showModal = false;

    const getAllTenantBreakdownPromise = (start, end, includeWeekends) => {
        if(isWeekend(pickerStartDate) && !includeWeekends) {
            tenantBreakdownByBucketData = null;
            return;
        }
        getTenantBreakdownByBucketPromise(start);
        // When only 1 day is selected, to calculate the average, we'll fetch data from the previous 5 days
        getTenantBreakdownSummedPromise(subDays(start, 5), start, includeWeekends);
    }

    const getTenantBreakdownByBucketPromise = (start) => {
        const dates = [zonedTimeToUtc(start, 'America/Chicago')]
        feGatewaySdk
            .getTenantBreakdownByBucket({
                buildingCode: buildingCodeDemoList.includes(buildingCode.toLowerCase()) ? "wwsf1" : buildingCode.toLowerCase(),
                dates,
            })
            .then((d: any) => {
                tenantBreakdownByBucketData = d;
            })
    }

    const getTenantBreakdownSummedPromise = (start, end, includeWeekends) => {
        feGatewaySdk
            .getTenantBreakdownSummed({
                buildingCode: buildingCodeDemoList.includes(buildingCode.toLowerCase()) ? "wwsf1" : buildingCode.toLowerCase(),
                fromDate: zonedTimeToUtc(start, 'America/Chicago'),
                toDate: zonedTimeToUtc(end, 'America/Chicago'),
                includeWeekends
            })
            .then((d: any) => {
                tenantBreakdownSummedData = d;
            })
    }

    const getTenantByValue = (tenantValues, previousTenantValuesByKey) => {
        return tenantValues.map(tenant => {
            const name = tenant?.name || "Unknown";
            return {
                label: name,
                value: tenant?.count || 0,
                averageValue: previousTenantValuesByKey ? previousTenantValuesByKey[name]?.count || undefined : undefined,
            }
        }).sort((l, r) => r.value - l.value)
    }

    const getTenantByLabel = (tenantValues, previousTenantValuesByKey) => {
        return tenantValues.map(tenant => {
            const name = tenant?.name || "Unknown";
            return {
                label: name,
                value: tenant?.count || 0,
                averageValue: previousTenantValuesByKey ? previousTenantValuesByKey[name]?.count || undefined : undefined,
            }
        }).sort((a,b) => {
            if(a.label > b.label) {return 1}
            if(a.label < b.label) {return -1}
            return 0
        }) || []
    }

    $: tenantBreakdownPromise = rangeDays === 1 ? getAllTenantBreakdownPromise(pickerStartDate, pickerEndDate, includeWeekends) : getTenantBreakdownSummedPromise(pickerStartDate, pickerEndDate, includeWeekends);

    $: tenantsByBuckets = tenantBreakdownByBucketData?.building.tenantBreakdownByBucket[0].breakdown.tenants || [];
    $: tenantsSummed = tenantBreakdownSummedData?.building.tenantBreakdownSummed.tenants || [];
    $: averageTenantByKey = tenantsSummed.reduce((acc, item) => {
        acc[item['name']] = {
            ...item,
            count: Math.round(item.count / 5)
        };
        return acc;
    }, {});

    $: dataSortedByValue = rangeDays === 1 ? getTenantByValue(tenantsByBuckets, !isToday(pickerStartDate) ? averageTenantByKey: null) : getTenantByValue(tenantsSummed, null)
    $: dataSortedByLabel = rangeDays === 1 ? getTenantByLabel(tenantsByBuckets, !isToday(pickerStartDate) ? averageTenantByKey: null) : getTenantByLabel(tenantsSummed, null)
</script>
  
<style>
    .modal-header {
        font-weight: 600;
        font-size: 22px;
        color: #1A1A1A;
        margin-top: 0;
    }

    img {
        width: 100%;
    }

    .graph-content-wrapper {
        padding: 0 2em 1.5em 2em;
        width: 100%;
        height: 25rem;
    }
    .graph-wrapper {
        height: 19em;
        overflow: hidden;
    }
    .graph-subtitle {
      color: #4e4e4e;
      font-size: 0.75rem;
      padding-bottom: 2rem;
    }
</style>

{#if showModal}
    <Modal on:close="{() => showModal = false}">
        <h2 slot="header" class="modal-header">
            Breakdown: Data Explanation
        </h2>
        <img src={tenantBreakdownImage} alt="test" />
    </Modal>
{/if}
<Snapshot title="Breakdown" tooltip={true} onTooltipClick={() => showModal = true}>
    <div class="graph-content-wrapper">
        <div class="graph-subtitle">
            How are groups occupying their space and how does that compare to the all-time average for weekdays?
        </div>
        <TabPage showDropShadow={false}>
            <SubPage id="service-breakdown" title="Occupancy">
                <div class="graph-wrapper">
                    {#if dataSortedByValue.length > 0}
                        <HorizontalBarGraph data={dataSortedByValue} />
                    {:else }
                        <span>No value to display</span>
                    {/if}
                </div>
            </SubPage>
            <SubPage id="tenant-breakdown" title="Name">
                <div class="graph-wrapper">
                    {#if dataSortedByLabel.length > 0}
                        <HorizontalBarGraph data={dataSortedByLabel} />
                    {:else }
                        <span>No value to display</span>
                    {/if}
                </div>
            </SubPage>
        </TabPage>
    </div>
</Snapshot>
